import { createPortal } from "react-dom";
import "./createSheet.css";
import { prepareImage } from "@/components/story/prepareMedia";
import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Camera, Bookmark, Share2, Gift, MessageCircle, MapPin, Users, Check, Accessibility, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import { PostCard } from "@/components/PostCard";
import { MissionCard } from "@/components/MissionCard";
import { toast } from "sonner";
import { api } from "@/App";
import { StoriesBar } from "@/components/StoriesBar";
import { ProSuggestionFeedCard } from "@/components/ProSuggestionFeedCard";
import { MentionSuggestions, applyMention } from "@/components/MentionSuggestions";
import { TagSuggestions, applyTag } from "@/components/TagSuggestions";
import { GoldenRingAvatar } from "@/components/GoldenRingAvatar";
import { LikesListModal, GiftsListModal } from "@/components/PhotoInteractionModals";
import ImageCropper from "@/components/ImageCropper";
import MediaEditor from "@/components/editor/MediaEditor";
import MediaUploadOverlay from "@/components/MediaUploadOverlay";
import { uploadVideoChunks, waitForJob } from "@/utils/videoUpload";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";
import { useLang } from "@/i18n";


export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ADE Pro suggestions interleaved in the feed
  useEffect(() => {
    api.get('/professionals/suggestions?limit=6')
      .then((r) => {
        const list = r.data.suggestions || [];
        setProSuggestions(list);
        if (list.length > 0) {
          api.post('/professionals/track', {
            events: list.map((p) => ({ professional_user_id: p.user_id, event_type: 'impression', source: 'suggestions' })),
          }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [discFeed, setDiscFeed] = useState({ pinned: [], discussions: [] });
  const [missions, setMissions] = useState([]);
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [discTitle, setDiscTitle] = useState("");
  const [discText, setDiscText] = useState("");

  const loadDiscussions = useCallback(() => {
    api.get("/discussions/feed").then((r) => setDiscFeed(r.data)).catch(() => {});
    const loadMissions = (coords) =>
      api.get("/missions/available", { params: coords || {} })
        .then((r) => setMissions(r.data.missions || [])).catch(() => {});
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => loadMissions({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
        () => loadMissions(),
        { enableHighAccuracy: false, maximumAge: 300000, timeout: 4000 }
      );
    } else loadMissions();
  }, []);

  useEffect(() => {
    loadDiscussions();
    const iv = setInterval(loadDiscussions, 30000);
    return () => clearInterval(iv);
  }, [loadDiscussions]);

  const createDiscussion = async () => {
    try {
      const r = await api.post("/discussions", { title: discTitle.trim(), opening_text: discText.trim() });
      setShowNewDiscussion(false); setDiscTitle(""); setDiscText("");
      navigate(`/app/discussion/${r.data.discussion.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const [proSuggestions, setProSuggestions] = useState([]);
  const [highlightPost, setHighlightPost] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [upJob, setUpJob] = useState(null);
  const [newCaption, setNewCaption] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [likeAnimations, setLikeAnimations] = useState({});
  const [savedPosts, setSavedPosts] = useState({});
  const [showShareModal, setShowShareModal] = useState(null);
  const [connections, setConnections] = useState([]);
  const [isLogoSpinning, setIsLogoSpinning] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorImage, setEditorImage] = useState(null);
  const [editorMeta, setEditorMeta] = useState(null);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [editedMedia, setEditedMedia] = useState([]);
  const [postLocation, setPostLocation] = useState("");
  const [postAlt, setPostAlt] = useState("");
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showFeedPreview, setShowFeedPreview] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(null);
  const [showGiftsModal, setShowGiftsModal] = useState(null);
  
  const fileInputRef = useRef(null);
  
  const fetchFeed = async () => {
    try {
      const [feedRes, savedRes, connectionsRes] = await Promise.all([
        api.get('/feed'),
        api.get('/photos/saved'),
        api.get('/connections')
      ]);
      setPosts(feedRes.data.posts);
      
      // Build saved posts lookup
      const savedLookup = {};
      (savedRes.data.photos || []).forEach(p => { savedLookup[p.photo_id] = true; });
      setSavedPosts(savedLookup);
      
      setConnections(connectionsRes.data.connections || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchFeed();
  }, []);
  
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    e.target.value = '';
    if (!files.length) return;
    const video = files.find((f) => f.type.startsWith('video/') || /\.(mp4|mov)$/i.test(f.name));
    if (video) {
      if (files.length > 1) {
        toast.error("Un post video può contenere un solo video");
        return;
      }
      if (video.size > 200 * 1024 * 1024) {
        toast.error("Video troppo grande (max 200MB)");
        return;
      }
      const url = URL.createObjectURL(video);
      const probe = document.createElement('video');
      probe.preload = 'metadata';
      probe.src = url;
      probe.onloadedmetadata = () => {
        if (probe.duration > 185) {
          URL.revokeObjectURL(url);
          toast.error("Video troppo lungo (max 3 minuti)");
          return;
        }
        setEditedMedia([]);
        setTaggedUsers([]); setPostLocation(""); setPostAlt(""); setShowFeedPreview(false);
        setVideoFile({ file: video, url });
        setShowNewPost(true);
      };
      probe.onerror = () => { URL.revokeObjectURL(url); toast.error("Video non valido"); };
      return;
    }
    // Nessun limite di MB: le foto vengono ridotte qui sul telefono
    toast.loading("Preparo le foto...", { id: "prep-post" });
    const prepared = await Promise.all(files.map((f) => prepareImage(f, 2048, 2048)));
    toast.dismiss("prep-post");
    if (prepared.some((f) => !f)) {
      toast.error("Un'immagine non è leggibile, prova con un'altra");
      return;
    }
    const urls = await Promise.all(prepared.map((f) => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = (ev) => res(ev.target.result);
      reader.readAsDataURL(f);
    })));
    // Reset pipeline, edit photos one by one (crop → editor)
    setVideoFile(null);
    setEditedMedia([]);
    setTaggedUsers([]); setPostLocation(""); setPostAlt(""); setShowFeedPreview(false);
    setPendingQueue(urls.slice(1));
    setCropperImage(urls[0]);
    setShowCropper(true);
  };
  
  const handleCroppedImage = (croppedBlob) => {
    // After crop → full editor (text, stickers, draw, filters...)
    setEditorImage(URL.createObjectURL(croppedBlob));
    setShowEditor(true);
  };

  const handleEditorDone = (editedBlob, meta) => {
    setShowEditor(false);
    setEditorImage(null);
    setEditorMeta(meta || null);
    setEditedMedia((prev) => [...prev, { blob: editedBlob, url: URL.createObjectURL(editedBlob) }]);
    if (meta?.location && !postLocation) setPostLocation(meta.location);
    if (pendingQueue.length > 0) {
      const [next, ...rest] = pendingQueue;
      setPendingQueue(rest);
      setCropperImage(next);
      setShowCropper(true);
    } else {
      setShowNewPost(true);
    }
  };
  
  const handlePost = async () => {
    if (!editedMedia.length && !videoFile) return;
    
    setUploading(true);
    try {
      if (videoFile) {
        // Video: upload a chunk (fino a 200MB) → compressione server-side → pubblicazione
        setUpJob({ progress: 0, phase: "Caricamento video", step: 0 });
        const uploadId = await uploadVideoChunks(videoFile.file, {
          kind: 'post',
          onProgress: (p) => setUpJob({ progress: p * 55, phase: "Caricamento video", step: 0 }),
        });
        const { data } = await api.post('/upload/post/video', {
          upload_id: uploadId,
          caption: newCaption,
          location: postLocation,
          alt_text: postAlt,
          tagged_user_ids: taggedUsers.map((u) => u.user_id).join(','),
        });
        await waitForJob(data.job_id, (p, phase) => setUpJob({
          progress: 55 + p * 45,
          phase: phase || "Compressione video",
          step: p >= 0.99 ? 2 : 1,
        }));
      } else {
        const formData = new FormData();
        editedMedia.forEach((m, i) => {
          formData.append('files', new File([m.blob], `post_${Date.now()}_${i}.jpg`, { type: 'image/jpeg' }));
        });
        formData.append('caption', newCaption);
        formData.append('location', postLocation);
        formData.append('alt_text', postAlt);
        formData.append('tagged_user_ids', taggedUsers.map((u) => u.user_id).join(','));
        await api.post('/upload/post', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowNewPost(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setNewCaption("");
      setEditedMedia([]);
      setVideoFile(null);
      setTaggedUsers([]); setPostLocation(""); setPostAlt(""); setShowFeedPreview(false);
      fetchFeed();
      toast.success(videoFile ? "Video pubblicato e compresso!" : "Pubblicato!");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || "Errore nel caricamento");
    } finally {
      setUploading(false);
      setUpJob(null);
    }
  };
  
  const handleLike = async (photoId) => {
    // Trigger animation
    setLikeAnimations(prev => ({ ...prev, [photoId]: true }));
    setTimeout(() => setLikeAnimations(prev => ({ ...prev, [photoId]: false })), 600);
    
    try {
      const response = await api.post(`/photos/${photoId}/like`);
      setPosts(prev => prev.map(p => 
        p.photo_id === photoId 
          ? { ...p, is_liked: response.data.liked, likes_count: response.data.likes_count }
          : p
      ));
    } catch (error) {
      console.error(error);
    }
  };
  
  const handleDoubleTapLike = (photoId, isLiked) => {
    if (!isLiked) {
      handleLike(photoId);
    }
  };
  
  const handleComment = async (photoId, content) => {
    if (!content?.trim()) return false;
    
    try {
      const response = await api.post(`/photos/${photoId}/comments`, { content: content.trim() });
      
      setPosts(prev => prev.map(p => {
        if (p.photo_id === photoId) {
          return {
            ...p,
            comments_count: p.comments_count + 1,
            recent_comments: [...p.recent_comments, {
              comment_id: response.data.comment_id,
              content: content.trim(),
              user: response.data.user
            }].slice(-3)
          };
        }
        return p;
      }));
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };
  
  const handleSave = async (photoId) => {
    try {
      const response = await api.post(`/photos/${photoId}/save`);
      setSavedPosts(prev => ({ ...prev, [photoId]: response.data.saved }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };
  
  const handleShare = async (photoId, recipientId) => {
    try {
      await api.post(`/photos/${photoId}/share/${recipientId}`);
      setShowShareModal(null);
      toast.success(String(photoId).startsWith("disc_") ? t("discussionShared") : t("postShared"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella condivisione");
    }
  };
  
  const getAvatarRingClass = (isVerified) => {
    return isVerified ? 'ring-2 ring-offset-1 ring-[#D4A574]' : '';
  };
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "ora";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}g`;
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };
  
  // Get current user for stories
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    api.get('/auth/me').then(res => setCurrentUser(res.data)).catch(() => {});
  }, []);
  
  // Scroll to specific post (and comment) from notification deep-link
  useEffect(() => {
    const postId = searchParams.get("post");
    const commentId = searchParams.get("comment");
    if (postId && posts.length > 0 && !loading) {
      setHighlightPost(postId);
      // Clear the param
      setSearchParams({}, { replace: true });

      const highlightComment = (cid) => {
        const cEl = document.querySelector(`[data-comment-id="${cid}"]`);
        if (!cEl) return false;
        cEl.scrollIntoView({ behavior: "smooth", block: "center" });
        cEl.classList.add("bg-amber-100", "dark:bg-amber-500/20", "rounded-md", "px-1", "transition-colors");
        setTimeout(() => cEl.classList.remove("bg-amber-100", "dark:bg-amber-500/20", "px-1"), 3500);
        return true;
      };

      // Scroll to the post
      setTimeout(async () => {
        const el = document.querySelector(`[data-post-id="${postId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // Flash highlight effect
          el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2");
            setHighlightPost(null);
          }, 3000);
          // Deep-link to the exact comment: expand full comment list if needed
          if (commentId && !highlightComment(commentId)) {
            try {
              const res = await api.get(`/photos/${postId}/comments`);
              const all = res.data.comments || [];
              if (all.some((c) => c.comment_id === commentId)) {
                setPosts((prev) => prev.map((p) => p.photo_id === postId ? { ...p, recent_comments: all } : p));
                setTimeout(() => highlightComment(commentId), 400);
              }
              // Comment deleted -> graceful fallback: stay on highlighted post
            } catch { /* fallback: post highlight only */ }
          }
        }
      }, 300);
    }
  }, [posts, loading, searchParams]);
  
  if (loading) {
    return (
      <div className="page-content bg-gradient-to-b from-white via-white to-gray-50/50" data-testid="feed-skeleton">
        <div className="px-4 py-4 space-y-8">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="space-y-2">
                  <div className="h-3 w-28 rounded bg-gray-200" />
                  <div className="h-2 w-16 rounded bg-gray-100" />
                </div>
              </div>
              <div className="aspect-square rounded-xl bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Refresh sound
  const playRefreshSound = () => {
    try {
      const sound = new Audio('/sounds/refresh.wav');
      sound.volume = 0.3;
      sound.play().catch(() => {});
    } catch (e) {}
  };
  
  const handleLogoClick = async () => {
    if (isLogoSpinning) return; // Prevent multiple clicks
    
    setIsLogoSpinning(true);
    
    // Play refresh sound
    playRefreshSound();
    
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Refresh the feed
    try {
      const response = await api.get('/feed');
      setPosts(response.data.posts || []);
      // No toast - sound provides feedback
    } catch (error) {
      console.error('Error refreshing feed:', error);
      toast.error("Errore nell'aggiornamento");
    }
    
    // Stop spinning after 3 seconds minimum
    setTimeout(() => setIsLogoSpinning(false), 3000);
  };
  
  // Discussions mixed with Posts: followed/pinned Discussions get personal ranking priority
  const feedItems = [
    ...(discFeed.pinned || []),
    ...[...posts, ...(discFeed.discussions || [])].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    ),
  ];

  return (
    <div className="page-content bg-gradient-to-b from-white via-white to-gray-50/50" data-testid="home-page">
      {/* Header - Fixed, ADE Here, Now. Brand Identity */}
      <div className="sticky top-0 safe-top z-40 bg-gradient-to-br from-white via-orange-50/20 to-pink-50/30 backdrop-blur-xl border-b border-gray-100/50 shadow-sm">
        <div className="flex items-center px-4 py-3">
          {/* Logo and brand - ADE Here, Now. - Logo clickable to refresh */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleLogoClick}
              className="focus:outline-none active:scale-95 transition-transform"
              data-testid="logo-refresh-btn"
            >
              <img 
                src="/images/ade-logo-extracted.png" 
                alt="ADE" 
                className={`object-contain drop-shadow-lg transition-transform ${isLogoSpinning ? 'animate-spin-slow' : 'hover:scale-105'}`}
                style={{ 
                  width: '56px', 
                  height: '56px',
                  minWidth: '56px',
                  minHeight: '56px'
                }}
              />
            </button>
            <div className="flex flex-col">
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-extrabold bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 bg-clip-text text-transparent tracking-tight">
                  ADE
                </span>
              </div>
              <span className="text-[10px] text-gray-500 font-medium tracking-widest">Here, Now.</span>
            </div>
          </div>
          
          <div className="ml-auto" />

          {/* New content button (+) — Post or Discussion */}
          <button
            onClick={() => setShowCreateSheet(true)}
            className="w-10 h-10 rounded-2xl bg-gradient-to-br from-orange-500 via-pink-500 to-purple-500 flex items-center justify-center shadow-lg shadow-pink-200/50 active:scale-95 transition-transform"
            data-testid="new-post-btn"
          >
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
          </button>
          
          {/* Hidden file input for new posts */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/quicktime"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>
      
      {/* Stories / Mood Bar */}
      <StoriesBar currentUser={currentUser} deepLinkStoryId={new URLSearchParams(window.location.search).get("story")} />

      {/* Create sheet — from the existing (+) */}
      {showCreateSheet && createPortal(
        <div className="cs-back" onClick={() => setShowCreateSheet(false)} data-testid="create-sheet-backdrop">
          <div className="cs-sheet" data-testid="create-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="cs-grab" />
            <p className="cs-title">Cosa vuoi pubblicare</p>

            <button className="cs-row" data-kind="post" data-testid="create-post-option"
              onClick={() => { setShowCreateSheet(false); fileInputRef.current?.click(); }}>
              <span className="cs-ico"><Camera className="w-5 h-5" strokeWidth={2.4} /></span>
              <span className="flex-1 min-w-0">
                <span className="cs-row-t block">Post</span>
                <span className="cs-row-s block">Foto o video dal tuo rullino</span>
              </span>
              <ChevronRight className="w-4 h-4 cs-chev" />
            </button>

            <button className="cs-row" data-kind="disc" data-testid="create-discussion-option"
              onClick={() => { setShowCreateSheet(false); setShowNewDiscussion(true); }}>
              <span className="cs-ico"><MessageCircle className="w-5 h-5" strokeWidth={2.4} /></span>
              <span className="flex-1 min-w-0">
                <span className="cs-row-t block">Discussion</span>
                <span className="cs-row-s block">Apri un tema e fai parlare la zona</span>
              </span>
              <ChevronRight className="w-4 h-4 cs-chev" />
            </button>

            <button className="cs-cancel" onClick={() => setShowCreateSheet(false)} data-testid="create-cancel-option">
              Annulla
            </button>
          </div>
        </div>,
        document.body
      )}

      {showNewDiscussion && createPortal(
        <div className="cs-back" onClick={() => setShowNewDiscussion(false)} data-testid="new-discussion-backdrop">
          <div className="cs-sheet" data-testid="new-discussion-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="cs-grab" />
            <p className="cs-title">{t('newDiscussion')}</p>
            <input value={discTitle} onChange={(e) => setDiscTitle(e.target.value)} maxLength={120}
              placeholder={t('discTitlePh')} data-testid="discussion-title-input" className="cs-field" />
            <textarea value={discText} onChange={(e) => setDiscText(e.target.value)} maxLength={2000} rows={3}
              placeholder={t('discOpeningPh')} data-testid="discussion-opening-input" className="cs-field" />
            <p className="cs-count">{discText.length}/2000</p>
            <button onClick={createDiscussion} disabled={!discTitle.trim() || !discText.trim()}
              data-testid="publish-discussion-btn" className="cs-cta">
              {t('publishDiscussion')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Feed */}
      <div className="pb-24">
        {feedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-100 via-orange-100 to-yellow-100 flex items-center justify-center animate-pulse">
                <Camera className="w-10 h-10 text-orange-400" strokeWidth={1.5} />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold shadow-lg">
                +
              </div>
            </div>
            <p className="text-gray-700 font-medium mb-1">{t('startSharing')}</p>
            <p className="text-sm text-gray-400 text-center mb-5">{t('publishFirstPhoto')}</p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 text-white px-6 shadow-lg shadow-orange-200/50"
            >
              <Camera className="w-4 h-4 mr-2" />
              {t('publishPhoto')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 pt-1">
            {feedItems.map((post, postIdx) => (
              <Fragment key={post.content_type === 'discussion' ? post.id : post.photo_id}>
              <PostCard
                post={post}
                isDiscussion={post.content_type === 'discussion'}
                highlight={highlightPost === post.photo_id}
                saved={!!savedPosts[post.content_type === 'discussion' ? post.id : post.photo_id]}
                likeAnimation={!!likeAnimations[post.photo_id]}
                formatTimeAgo={formatTimeAgo}
                onLike={handleLike}
                onDoubleTapLike={handleDoubleTapLike}
                onSave={handleSave}
                onComment={handleComment}
                onShare={(id) => setShowShareModal(id)}
                onOpenLikes={(id) => setShowLikesModal(id)}
                onOpenGifts={(id) => setShowGiftsModal(id)}
                onStateChange={loadDiscussions}
                onGiftSent={(id, amount, data) => {
                  setPosts(prev => prev.map(p =>
                    p.photo_id === id
                      ? { ...p, total_gifts: (p.total_gifts || 0) + amount, gift_value: (p.gift_value || 0) + (data?.coins_spent || 0) }
                      : p
                  ));
                }}
              />
              {/* ADE Mission card: dopo il 2° post, poi ogni 6 (attive + programmate) */}
              {missions.length > 0 && (postIdx % 6 === 1 || (postIdx === feedItems.length - 1 && feedItems.length < 2)) &&
                missions[Math.floor(postIdx / 6) % missions.length] && (
                <MissionCard mission={missions[Math.floor(postIdx / 6) % missions.length]}
                  onInterestChange={(cid, val) => setMissions(prev => prev.map(m => m.campaign_id === cid ? { ...m, my_interest: val } : m))} />
              )}
              {/* ADE Pro suggestion card: every 5 posts (or after the last post on short feeds) */}
              {proSuggestions.length > 0 && ((postIdx + 1) % 5 === 0 || (postIdx === feedItems.length - 1 && feedItems.length < 5)) && (
                <ProSuggestionFeedCard pro={proSuggestions[Math.floor(postIdx / 5) % proSuggestions.length]} />
              )}
              </Fragment>
            ))}
          </div>
        )}
      </div>
      
      {/* New Post Modal */}
      <Dialog open={showNewPost} onOpenChange={(open) => { setShowNewPost(open); if (!open) { setEditedMedia([]); setPendingQueue([]); setVideoFile(null); } }}>
        <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-3xl p-0 overflow-hidden max-h-[92dvh] flex flex-col z-[80]">
          <DialogHeader className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-pink-50 to-orange-50 shrink-0">
            <DialogTitle className="text-center text-base font-semibold bg-gradient-to-r from-pink-500 to-orange-500 bg-clip-text text-transparent">
              {videoFile ? 'Nuovo video' : `Nuovo post ${editedMedia.length > 1 ? `(${editedMedia.length} foto)` : ''}`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1">
          {!showFeedPreview ? (
            <>
              {/* Video preview */}
              {videoFile && (
                <div className="relative aspect-square bg-black" data-testid="composer-video-preview">
                  <video src={videoFile.url} className="w-full h-full object-contain" controls muted playsInline />
                </div>
              )}
              {/* Media carousel preview */}
              {editedMedia.length > 0 && (
                <div className="relative aspect-square bg-gray-100">
                  <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-full" data-testid="composer-carousel">
                    {editedMedia.map((m, i) => (
                      <img key={i} src={m.url} alt="" className="w-full h-full object-contain shrink-0 snap-center" />
                    ))}
                  </div>
                  {editedMedia.length > 1 && (
                    <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-semibold">
                      {editedMedia.length} foto
                    </div>
                  )}
                </div>
              )}
              
              <div className="p-4 space-y-3">
                <div className="relative">
                  <MentionSuggestions
                    text={newCaption}
                    onPick={(u) => setNewCaption((prev) => applyMention(prev, u.username))}
                    className="bottom-auto top-full mt-1 mb-0"
                  />
                  <TagSuggestions
                    text={newCaption}
                    onPick={(tag) => setNewCaption((prev) => applyTag(prev, tag))}
                    className="bottom-auto top-full mt-1 mb-0"
                  />
                  <Textarea
                    value={newCaption}
                    onChange={(e) => setNewCaption(e.target.value)}
                    placeholder={t('addCaption')}
                    className="border-0 resize-none focus-visible:ring-0 text-sm p-0 min-h-[52px]"
                    maxLength={500}
                    data-testid="post-caption-input"
                  />
                </div>
                <p className="text-xs text-gray-400 text-right -mt-2">{newCaption.length}/500</p>
                
                {/* Location */}
                <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
                  <MapPin className="w-4 h-4 text-gray-500" strokeWidth={2.2} />
                  <input
                    value={postLocation}
                    onChange={(e) => setPostLocation(e.target.value)}
                    placeholder="Aggiungi luogo"
                    className="flex-1 text-sm outline-none placeholder:text-gray-400"
                    data-testid="post-location-input"
                  />
                </div>
                
                {/* Tag people */}
                <div className="border-b border-gray-100 pb-2">
                  <button onClick={() => setShowTagPicker(!showTagPicker)} className="flex items-center gap-2 text-sm text-gray-700 w-full" data-testid="post-tag-btn">
                    <Users className="w-4 h-4 text-gray-500" strokeWidth={2.2} />
                    <span className="flex-1 text-left">
                      {taggedUsers.length > 0 ? taggedUsers.map((u) => u.name).join(', ') : 'Tagga persone'}
                    </span>
                    {showTagPicker
                      ? <ChevronUp className="w-4 h-4 text-gray-300" />
                      : <ChevronDown className="w-4 h-4 text-gray-300" />}
                  </button>
                  {showTagPicker && (
                    <div className="mt-2 max-h-36 overflow-y-auto space-y-1" data-testid="tag-picker">
                      {connections.length === 0 && <p className="text-xs text-gray-400 py-2">Nessun collegamento</p>}
                      {connections.map((conn) => {
                        const c = conn.user || conn;
                        const tagged = taggedUsers.some((u) => u.user_id === c.user_id);
                        return (
                          <button key={c.user_id}
                            onClick={() => setTaggedUsers((prev) => tagged ? prev.filter((u) => u.user_id !== c.user_id) : [...prev, { user_id: c.user_id, name: c.name }])}
                            className={`flex items-center gap-2 w-full p-1.5 rounded-lg text-left ${tagged ? 'bg-pink-50' : 'hover:bg-gray-50'}`}
                            data-testid={`tag-user-${c.user_id}`}>
                            <img src={c.picture || '/images/default-avatar.png'} alt="" className="w-7 h-7 rounded-full object-cover" />
                            <span className="text-sm flex-1">{c.name}</span>
                            {tagged && <Check className="w-3.5 h-3.5 text-pink-500" strokeWidth={3} />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Alt text (accessibility) */}
                <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
                  <Accessibility className="w-4 h-4 text-gray-500" strokeWidth={2.2} />
                  <input
                    value={postAlt}
                    onChange={(e) => setPostAlt(e.target.value)}
                    placeholder="Testo alternativo (accessibilità)"
                    className="flex-1 text-sm outline-none placeholder:text-gray-400"
                    maxLength={200}
                    data-testid="post-alt-input"
                  />
                </div>
              </div>
            </>
          ) : (
            /* Real feed preview */
            <div className="p-3" data-testid="feed-preview">
              <p className="text-xs text-gray-400 text-center mb-2 uppercase tracking-wider">Anteprima feed</p>
              <div className="border border-gray-100 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <img src={currentUser?.picture || '/images/default-avatar.png'} alt="" className="w-9 h-9 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{currentUser?.name}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      ora
                      {postLocation && (
                        <>
                          <span>·</span>
                          <MapPin className="w-3 h-3" strokeWidth={2.2} />
                          {postLocation}
                        </>
                      )}
                    </p>
                    {taggedUsers.length > 0 && <p className="text-[11px] text-gray-500">con {taggedUsers.map((u) => u.name).join(', ')}</p>}
                  </div>
                </div>
                <div className="relative aspect-square bg-gray-100">
                  <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-full">
                    {editedMedia.map((m, i) => (
                      <img key={i} src={m.url} alt={postAlt || ""} className="w-full h-full object-contain shrink-0 snap-center" />
                    ))}
                  </div>
                  {editedMedia.length > 1 && (
                    <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-semibold">1/{editedMedia.length}</div>
                  )}
                </div>
                {newCaption && (
                  <p className="px-3 py-2 text-sm"><span className="font-semibold">{currentUser?.name}</span> {newCaption}</p>
                )}
              </div>
            </div>
          )}
          </div>
          
          <div className="p-4 pt-2 shrink-0 border-t border-gray-50">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFeedPreview(!showFeedPreview)}
                className="h-11 rounded-full px-4 bg-white text-gray-900 border-gray-300 hover:bg-gray-100"
                data-testid="toggle-feed-preview-btn"
              >
                {showFeedPreview ? 'Modifica' : 'Anteprima'}
              </Button>
              <Button
                onClick={handlePost}
                disabled={uploading || (editedMedia.length === 0 && !videoFile)}
                className="flex-1 h-11 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 hover:opacity-90 text-white font-semibold shadow-lg shadow-orange-200/50"
                data-testid="publish-post-btn"
              >
                {uploading ? (
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-white animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-white animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full bg-white animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                ) : 'Condividi'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Share Post Modal */}
      <Dialog open={!!showShareModal} onOpenChange={() => setShowShareModal(null)}>
        <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-3xl p-0 overflow-hidden">
          <DialogHeader className="px-4 py-3 border-b border-gray-100">
            <DialogTitle className="text-center text-base font-semibold">
              Condividi con
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[60vh] overflow-y-auto p-4">
            {connections.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">{t("noConnectionsShort")}</p>
                <p className="text-xs mt-1">{t("connectToShare")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {connections.map((connection) => (
                  <button
                    key={connection.connection_id}
                    onClick={() => handleShare(showShareModal, connection.user?.user_id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    data-testid={`share-user-${connection.user?.user_id}`}
                  >
                    <GoldenRingAvatar 
                      user={connection.user}
                      size="sm"
                    />
                    <span className="flex-1 text-left font-medium text-gray-900">
                      {connection.user?.name}
                    </span>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
                      <path 
                        d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* HUD cinematico di caricamento video */}
      {upJob && (
        <MediaUploadOverlay
          progress={upJob.progress}
          phase={upJob.phase}
          step={upJob.step}
          note="Il video viene compresso in alta qualità sui server ADE. Non chiudere l'app."
        />
      )}


      {/* Image Cropper Modal */}
      <ImageCropper
        open={showCropper}
        onClose={() => {
          setShowCropper(false);
          setCropperImage(null);
        }}
        imageSrc={cropperImage}
        onCropComplete={handleCroppedImage}
        aspectRatio={4/5}
        allowAspectChange={true}
        title="Ritaglia foto"
        cropShape="rect"
      />

      {/* Full media editor for Posts */}
      <MediaEditor
        open={showEditor}
        imageSrc={editorImage}
        mode="post"
        onClose={() => { setShowEditor(false); setEditorImage(null); }}
        onDone={handleEditorDone}
      />
      
      {/* Likes List Modal - Who liked this photo */}
      <LikesListModal
        isOpen={!!showLikesModal}
        onClose={() => setShowLikesModal(null)}
        photoId={showLikesModal}
      />
      
      {/* Gifts List Modal - What gifts were sent to this photo */}
      <GiftsListModal
        isOpen={!!showGiftsModal}
        onClose={() => setShowGiftsModal(null)}
        photoId={showGiftsModal}
      />
    </div>
  );
}
